import { useEffect, useState, type ReactNode } from 'react';
import { Button } from '@web/components/Button';
import { SectionLabel } from '@web/components/Feedback';
import { useSession } from '@web/hooks/useSession';
import { cn } from '@web/lib/cn';
import {
  cannedResponsesSchema,
  DEFAULT_WEIGHTS,
  DEFAULT_WORKING_HOURS,
  escalationRulesSchema,
  knowledgeBaseSchema,
  SETTING_KEYS,
  temperatureWeightsSchema,
  templatesSchema,
  workingHoursSchema,
  type CannedResponse,
  type EscalationRule,
  type KnowledgeEntry,
  type TemperatureWeights,
  type WaTemplate,
  type WorkingHours,
} from './schema';
import { useSaveSetting, useSetting } from './useSettings';

/**
 * Settings.
 *
 * Every section reads and writes one jsonb key. Writes are admin-only on the
 * server, so the console mirrors that: an agent sees the values (the board is
 * rendered from some of them and hiding them would make the product harder to
 * reason about) but every control is disabled and says why. Client-side role
 * checks are cosmetic — the 403 is the real gate.
 */
export function SettingsPage() {
  const session = useSession();
  const admin = session.data?.role === 'admin';

  return (
    <div className="h-full overflow-auto [overscroll-behavior:contain]">
      <div className="border-b border-rule bg-panel px-4 py-3">
        <h1 className="text-sm font-semibold">Settings</h1>
        <p className="mt-0.5 text-[11px] text-ink-faint">
          {admin
            ? 'Changes take effect without a deploy.'
            : 'Read-only — an administrator can change these.'}
        </p>
      </div>

      <div className="mx-auto max-w-3xl px-4 py-4">
        <TemperatureWeightsSection admin={admin} />
        <WorkingHoursSection admin={admin} />
        <EscalationSection admin={admin} />
        <CannedSection admin={admin} />
        <KnowledgeSection admin={admin} />
        <TemplatesSection admin={admin} />
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Shared section chrome                                                       */
/* -------------------------------------------------------------------------- */

function Section({
  title,
  note,
  admin,
  dirty,
  onSave,
  saving,
  children,
}: {
  title: string;
  note: string;
  admin: boolean;
  dirty: boolean;
  onSave: () => void;
  saving: boolean;
  children: ReactNode;
}) {
  return (
    <section className="mb-6 border border-rule bg-panel">
      <header className="flex items-center justify-between gap-3 border-b border-rule px-3 py-2">
        <div>
          <SectionLabel>{title}</SectionLabel>
          <p className="mt-0.5 text-[11px] text-ink-faint">{note}</p>
        </div>
        <Button
          tone="primary"
          size="default"
          disabled={!admin || !dirty || saving}
          onClick={onSave}
          title={admin ? undefined : 'Administrators only'}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </header>
      <div className="p-3">{children}</div>
    </section>
  );
}

function Input({
  value,
  onChange,
  disabled,
  label,
  mono,
  placeholder,
}: {
  value: string;
  onChange: (next: string) => void;
  disabled: boolean;
  label: string;
  mono?: boolean;
  placeholder?: string;
}) {
  return (
    <label className="flex min-w-0 flex-1 flex-col gap-1">
      <span className="font-mono text-[10px] tracking-[0.1em] text-ink-faint uppercase">
        {label}
      </span>
      <input
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        className={cn(
          'h-9 w-full border border-rule-strong bg-ground px-2 disabled:opacity-60',
          mono === true && 'font-mono tabular-nums',
        )}
      />
    </label>
  );
}

/** A local draft that resyncs whenever the server value changes underneath it. */
function useDraft<T>(serverValue: T | undefined): [T | undefined, (next: T) => void, boolean] {
  const [draft, setDraft] = useState<T | undefined>(serverValue);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (dirty) return;
    setDraft(serverValue);
  }, [serverValue, dirty]);

  return [
    draft,
    (next: T) => {
      setDraft(next);
      setDirty(true);
    },
    dirty,
  ];
}

/* -------------------------------------------------------------------------- */
/* Temperature weights — the one key the server already reads                  */
/* -------------------------------------------------------------------------- */

function TemperatureWeightsSection({ admin }: { admin: boolean }) {
  const setting = useSetting(
    SETTING_KEYS.temperatureWeights,
    temperatureWeightsSchema,
    DEFAULT_WEIGHTS,
  );
  const save = useSaveSetting(SETTING_KEYS.temperatureWeights);
  const [draft, setDraft, dirty] = useDraft<TemperatureWeights>(setting.data?.value);

  const weights = draft ?? DEFAULT_WEIGHTS;
  const total = weights.fit + weights.documents + weights.engagement + weights.intent;

  return (
    <Section
      title="Temperature weights"
      note="How a lead's score is blended. Read by the scorer on every turn."
      admin={admin}
      dirty={dirty}
      saving={save.isPending}
      onSave={() => {
        save.mutate(weights);
      }}
    >
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {(['fit', 'documents', 'engagement', 'intent'] as const).map((key) => (
          <Input
            key={key}
            label={key}
            mono
            disabled={!admin}
            value={String(weights[key])}
            onChange={(next) => {
              const parsed = Number(next);
              setDraft({ ...weights, [key]: Number.isFinite(parsed) ? parsed : 0 });
            }}
          />
        ))}
      </div>
      <p
        className={cn(
          'mt-2 font-mono text-[11px] tabular-nums',
          total === 100 ? 'text-ink-faint' : 'text-warm',
        )}
      >
        TOTAL {total}
        {total === 100 ? '' : ' — the four weights are meant to add to 100'}
      </p>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/* Working hours                                                               */
/* -------------------------------------------------------------------------- */

const DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

function WorkingHoursSection({ admin }: { admin: boolean }) {
  const setting = useSetting(SETTING_KEYS.workingHours, workingHoursSchema, DEFAULT_WORKING_HOURS);
  const save = useSaveSetting(SETTING_KEYS.workingHours);
  const [draft, setDraft, dirty] = useDraft<WorkingHours>(setting.data?.value);
  const hours = draft ?? DEFAULT_WORKING_HOURS;

  return (
    <Section
      title="Working hours"
      note="Outside these, the assistant parks with `out_of_hours` instead of guessing."
      admin={admin}
      dirty={dirty}
      saving={save.isPending}
      onSave={() => {
        save.mutate(hours);
      }}
    >
      <div className="mb-3 max-w-xs">
        <Input
          label="timezone"
          mono
          disabled={!admin}
          value={hours.timezone}
          onChange={(next) => {
            setDraft({ ...hours, timezone: next });
          }}
        />
      </div>

      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-rule">
            {['Day', 'Open', 'From', 'To'].map((heading) => (
              <th
                key={heading}
                scope="col"
                className="py-1 font-mono text-[10px] font-medium tracking-[0.1em] text-ink-faint uppercase"
              >
                {heading}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {DAYS.map((day) => (
            <tr key={day} className="border-b border-rule last:border-b-0">
              <td className="py-1.5 capitalize">{day}</td>
              <td className="py-1.5">
                <input
                  type="checkbox"
                  disabled={!admin}
                  checked={hours[day].open}
                  aria-label={`${day} open`}
                  onChange={(event) => {
                    setDraft({ ...hours, [day]: { ...hours[day], open: event.target.checked } });
                  }}
                  className="size-4 accent-[var(--ink)]"
                />
              </td>
              {(['from', 'to'] as const).map((edge) => (
                <td key={edge} className="py-1.5">
                  <input
                    type="time"
                    disabled={!admin || !hours[day].open}
                    value={hours[day][edge]}
                    aria-label={`${day} ${edge}`}
                    onChange={(event) => {
                      setDraft({
                        ...hours,
                        [day]: { ...hours[day], [edge]: event.target.value },
                      });
                    }}
                    className="h-8 border border-rule-strong bg-ground px-1 font-mono tabular-nums disabled:opacity-50"
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/* Escalation rules                                                            */
/* -------------------------------------------------------------------------- */

const ESCALATION_REASONS: EscalationRule['when'][] = [
  'asked_for_human',
  'frustrated',
  'ai_stuck',
  'low_confidence',
  'money_question',
  'missing_data',
  'complaint',
  'ai_error',
  'out_of_hours',
];

function EscalationSection({ admin }: { admin: boolean }) {
  const setting = useSetting(SETTING_KEYS.escalation, escalationRulesSchema, []);
  const save = useSaveSetting(SETTING_KEYS.escalation);
  const [draft, setDraft, dirty] = useDraft<EscalationRule[]>(setting.data?.value);
  const rules = draft ?? [];

  const byReason = new Map(rules.map((rule) => [rule.when, rule]));
  const merged: EscalationRule[] = ESCALATION_REASONS.map(
    (reason) =>
      byReason.get(reason) ?? {
        id: reason,
        when: reason,
        enabled: true,
        graceMinutes: 0,
        note: '',
      },
  );

  return (
    <Section
      title="Escalation rules"
      note="Which signals hand a conversation to a human, and how long the assistant may keep trying first."
      admin={admin}
      dirty={dirty}
      saving={save.isPending}
      onSave={() => {
        save.mutate(merged);
      }}
    >
      <ul>
        {merged.map((rule) => (
          <li
            key={rule.when}
            className="flex flex-wrap items-center gap-3 border-b border-rule py-2 last:border-b-0"
          >
            <label className="flex min-w-[190px] items-center gap-2">
              <input
                type="checkbox"
                disabled={!admin}
                checked={rule.enabled}
                onChange={(event) => {
                  setDraft(
                    merged.map((item) =>
                      item.when === rule.when ? { ...item, enabled: event.target.checked } : item,
                    ),
                  );
                }}
                className="size-4 accent-[var(--ink)]"
              />
              <span className="font-mono text-[11px]">{rule.when}</span>
            </label>

            <label className="flex items-center gap-2">
              <span className="font-mono text-[10px] tracking-[0.08em] text-ink-faint uppercase">
                grace
              </span>
              <input
                type="number"
                min={0}
                max={120}
                disabled={!admin || !rule.enabled}
                value={rule.graceMinutes}
                aria-label={`${rule.when} grace minutes`}
                onChange={(event) => {
                  setDraft(
                    merged.map((item) =>
                      item.when === rule.when
                        ? { ...item, graceMinutes: Number(event.target.value) }
                        : item,
                    ),
                  );
                }}
                className="h-8 w-16 border border-rule-strong bg-ground px-1 font-mono tabular-nums disabled:opacity-50"
              />
              <span className="text-[11px] text-ink-faint">min</span>
            </label>
          </li>
        ))}
      </ul>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/* Canned responses                                                            */
/* -------------------------------------------------------------------------- */

function CannedSection({ admin }: { admin: boolean }) {
  const setting = useSetting(SETTING_KEYS.canned, cannedResponsesSchema, []);
  const save = useSaveSetting(SETTING_KEYS.canned);
  const [draft, setDraft, dirty] = useDraft<CannedResponse[]>(setting.data?.value);
  const items = draft ?? [];

  return (
    <Section
      title="Canned responses"
      note="Typed as /shortcut in the composer."
      admin={admin}
      dirty={dirty}
      saving={save.isPending}
      onSave={() => {
        save.mutate(items);
      }}
    >
      <ul>
        {items.map((item, index) => (
          <li key={item.id} className="mb-3 border-b border-rule pb-3 last:border-b-0">
            <div className="flex gap-3">
              <div className="w-40">
                <Input
                  label="shortcut"
                  mono
                  disabled={!admin}
                  value={item.shortcut}
                  onChange={(next) => {
                    setDraft(
                      items.map((row, i) => (i === index ? { ...row, shortcut: next } : row)),
                    );
                  }}
                />
              </div>
              <label className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="font-mono text-[10px] tracking-[0.1em] text-ink-faint uppercase">
                  body
                </span>
                <textarea
                  rows={2}
                  disabled={!admin}
                  value={item.body}
                  onChange={(event) => {
                    setDraft(
                      items.map((row, i) =>
                        i === index ? { ...row, body: event.target.value } : row,
                      ),
                    );
                  }}
                  className="w-full border border-rule-strong bg-ground px-2 py-1.5 disabled:opacity-60"
                />
              </label>
              <Button
                tone="danger"
                size="default"
                disabled={!admin}
                onClick={() => {
                  setDraft(items.filter((_, i) => i !== index));
                }}
              >
                Remove
              </Button>
            </div>
          </li>
        ))}
      </ul>

      <Button
        size="default"
        disabled={!admin}
        onClick={() => {
          setDraft([...items, { id: `canned-${String(Date.now())}`, shortcut: 'new', body: '' }]);
        }}
      >
        Add response
      </Button>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/* Knowledge base                                                              */
/* -------------------------------------------------------------------------- */

function KnowledgeSection({ admin }: { admin: boolean }) {
  const setting = useSetting(SETTING_KEYS.knowledge, knowledgeBaseSchema, []);
  const save = useSaveSetting(SETTING_KEYS.knowledge);
  const [draft, setDraft, dirty] = useDraft<KnowledgeEntry[]>(setting.data?.value);
  const entries = draft ?? [];

  return (
    <Section
      title="Knowledge base"
      note="Answers the assistant may reuse verbatim. Never put a salary or a fee here — those are rendered from the demand record."
      admin={admin}
      dirty={dirty}
      saving={save.isPending}
      onSave={() => {
        save.mutate(entries);
      }}
    >
      <ul>
        {entries.map((entry, index) => (
          <li key={entry.id} className="mb-3 border-b border-rule pb-3 last:border-b-0">
            <Input
              label="question"
              disabled={!admin}
              value={entry.question}
              onChange={(next) => {
                setDraft(entries.map((row, i) => (i === index ? { ...row, question: next } : row)));
              }}
            />
            <label className="mt-2 flex flex-col gap-1">
              <span className="font-mono text-[10px] tracking-[0.1em] text-ink-faint uppercase">
                answer
              </span>
              <textarea
                rows={3}
                disabled={!admin}
                value={entry.answer}
                onChange={(event) => {
                  setDraft(
                    entries.map((row, i) =>
                      i === index ? { ...row, answer: event.target.value } : row,
                    ),
                  );
                }}
                className="w-full border border-rule-strong bg-ground px-2 py-1.5 disabled:opacity-60"
              />
            </label>
            <div className="mt-2 flex items-end gap-3">
              <Input
                label="tags (comma separated)"
                disabled={!admin}
                value={entry.tags.join(', ')}
                onChange={(next) => {
                  setDraft(
                    entries.map((row, i) =>
                      i === index
                        ? {
                            ...row,
                            tags: next
                              .split(',')
                              .map((tag) => tag.trim())
                              .filter((tag) => tag !== ''),
                          }
                        : row,
                    ),
                  );
                }}
              />
              <Button
                tone="danger"
                size="default"
                disabled={!admin}
                onClick={() => {
                  setDraft(entries.filter((_, i) => i !== index));
                }}
              >
                Remove
              </Button>
            </div>
          </li>
        ))}
      </ul>

      <Button
        size="default"
        disabled={!admin}
        onClick={() => {
          setDraft([
            ...entries,
            { id: `kb-${String(Date.now())}`, question: '', answer: '', tags: [] },
          ]);
        }}
      >
        Add entry
      </Button>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/* WhatsApp templates                                                          */
/* -------------------------------------------------------------------------- */

function TemplatesSection({ admin }: { admin: boolean }) {
  const setting = useSetting(SETTING_KEYS.templates, templatesSchema, []);
  const save = useSaveSetting(SETTING_KEYS.templates);
  const [draft, setDraft, dirty] = useDraft<WaTemplate[]>(setting.data?.value);
  const templates = draft ?? [];

  return (
    <Section
      title="Approved templates"
      note="The only thing deliverable once a candidate's 24-hour window has closed."
      admin={admin}
      dirty={dirty}
      saving={save.isPending}
      onSave={() => {
        save.mutate(templates);
      }}
    >
      <ul>
        {templates.map((template, index) => (
          <li key={template.id} className="mb-3 border-b border-rule pb-3 last:border-b-0">
            <div className="flex gap-3">
              <Input
                label="name"
                mono
                disabled={!admin}
                value={template.name}
                onChange={(next) => {
                  setDraft(templates.map((row, i) => (i === index ? { ...row, name: next } : row)));
                }}
              />
              <div className="w-24">
                <Input
                  label="lang"
                  mono
                  disabled={!admin}
                  value={template.language}
                  onChange={(next) => {
                    setDraft(
                      templates.map((row, i) => (i === index ? { ...row, language: next } : row)),
                    );
                  }}
                />
              </div>
              <Button
                tone="danger"
                size="default"
                disabled={!admin}
                onClick={() => {
                  setDraft(templates.filter((_, i) => i !== index));
                }}
              >
                Remove
              </Button>
            </div>
            <label className="mt-2 flex flex-col gap-1">
              <span className="font-mono text-[10px] tracking-[0.1em] text-ink-faint uppercase">
                body
              </span>
              <textarea
                rows={2}
                disabled={!admin}
                value={template.body}
                onChange={(event) => {
                  setDraft(
                    templates.map((row, i) =>
                      i === index ? { ...row, body: event.target.value } : row,
                    ),
                  );
                }}
                className="w-full border border-rule-strong bg-ground px-2 py-1.5 disabled:opacity-60"
              />
            </label>
          </li>
        ))}
      </ul>

      <Button
        size="default"
        disabled={!admin}
        onClick={() => {
          setDraft([
            ...templates,
            { id: `tpl-${String(Date.now())}`, name: '', language: 'en', body: '' },
          ]);
        }}
      >
        Add template
      </Button>
    </Section>
  );
}
